import React, { useEffect, useRef, useState } from 'react';
import { connect } from 'react-redux';

import * as Sentry from '@sentry/react';
import amplitude from 'amplitude-js';
import { toggleModalWithTimer, toggleSpinner } from 'apps/auth/actions/appAction';
import { toggleChat } from 'apps/auth/actions/chatAction';
import { addExtras, setExtra } from 'apps/auth/actions/extraAction';
import { updatePageMessages } from 'apps/auth/actions/messageAction';
import { Button } from 'apps/auth/components/common/Button/Button';
import { DangerouslyHtml } from 'apps/auth/components/common/DangerouslyHtml/DangerouslyHtml';
import { CommonPage } from 'apps/auth/components/common/Layout/CommonPage/CommonPage';
import { FixedBottomPanel } from 'apps/auth/components/common/Layout/FixedBottomPanel/FixedBottomPanel';
import { HeaderPanel } from 'apps/auth/components/common/Layout/HeaderPanel/HeaderPanel';
import { InfoPage } from 'apps/auth/components/common/Layout/InfoPage/InfoPage';
import { MarkedList } from 'apps/auth/components/common/MarkedList/MarkedList';
import { MarkedListItem } from 'apps/auth/components/common/MarkedList/MarkedListItem';
import OnfidoDocList from 'apps/auth/components/common/onfido/OnfidoDocList';
import Form from 'apps/auth/components/form-multi/Form';
import InputText from 'apps/auth/components/form-multi/InputText';
import { AMPLITUDE_API_KEY, getHelpMobileIdParams } from 'apps/auth/components/utils/AmplitudeUtils';
import {
    AF_RECIPIENT_EMAIL_CONTINUE_CLICKED,
    AF_RECIPIENT_IDENTITY_REQUEST_PROMO_CONTINUE_CLICKED,
    AF_RECIPIENT_ONFIDO_IN_REVIEW_PROMO_DONE_CLICKED,
    AF_RECIPIENT_PERSONAL_DATA_PROCESSING_AGREE_CLICKED,
    GET_HELP_CLICKED,
} from 'apps/auth/constants/amplitude/AmplitudeEvents';
import { SCENARIO_ID } from 'apps/auth/constants/amplitude/AmplitudeParamName';
import {
    KYC_FAILED_SCREEN,
    KYC_VERIFYING_SCREEN,
    ONFIDO_DOCUMENT_EXPLANATION_SCREEN,
    ONFIDO_DOCUMENTS_SCREEN,
    ONFIDO_SDK,
} from 'apps/auth/constants/ComponentNames';
import { ERROR } from 'apps/auth/constants/Constants';
import { HELP_MOBILE, LANDING_LINK } from 'apps/auth/constants/UrlConstants';
import { apiFetchData } from 'apps/auth/helpers/api';
import { logInfo } from 'apps/auth/helpers/clientLogger';
import { addOnfido } from 'apps/auth/helpers/DOM';
import { getLangCountryPrefix } from 'apps/auth/helpers/utils';
import { useApp, useExtra, useForm, useLanguage, usePageMessages } from 'apps/auth/hooks/selectors';
import { OnfidoContainer } from 'apps/auth/pages/kyc/OnfidoFramePage';
import { ConsentService } from 'apps/auth/services/ConsentService';
import { OnfidoService } from 'apps/auth/services/OnfidoService';

const AntiFraudRecipientFlowPage = (props) => {
    const INTRO_SCREEN = 'INTRO_SCREEN';
    const AGREEMENT_SCREEN = 'AGREEMENT_SCREEN';
    const EMAIL_SCREEN = 'EMAIL_SCREEN';
    const EXPIRED_SCREEN = 'EXPIRED_SCREEN';
    const ANTIFRAUD_SCENARIO_ID = 1000;

    const extra = useExtra();
    const { country } = useApp();
    const { lang } = useLanguage();

    const getFirstScreen = () => {
        if (extra.expired) {
            return EXPIRED_SCREEN;
        } else if (extra.fraudOnfidoInProgress) {
            return KYC_VERIFYING_SCREEN;
        } else if (extra.fraudOnfidoNeedToRetry || extra.fraudOnfidoTooManyAttempts) {
            return KYC_FAILED_SCREEN;
        } else {
            return INTRO_SCREEN;
        }
    };

    const onfidoOutRef = useRef(null);
    const [showOnfido, setShowOnfido] = useState(false);
    const [screen, setScreen] = useState(getFirstScreen());
    const messages = usePageMessages();
    const paymentInfo = extra.paymentInfo;
    const form = useForm('recipientEmail');

    useEffect(() => {
        if (__isProd__ && __isBrowser__) {
            const tryInitAmplitude = () => {
                if (!ConsentService.hasLoaded()) return;
                const { analytics } = ConsentService.getConsentState();
                if (analytics) {
                    amplitude.getInstance().init(AMPLITUDE_API_KEY, null, {
                        serverZone: 'EU',
                        serverZoneBasedApi: true,
                    });
                } else {
                    try {
                        amplitude.getInstance().setOptOut(true);
                    } catch (e) {}
                }
            };
            tryInitAmplitude();
        } else {
            amplitude.getInstance = () => {
                return {
                    setUserId: () => {},
                    setUserProperties: () => {},
                    logEvent: () => {},
                };
            };
        }
    }, []);

    useEffect(() => {
        if (showOnfido && typeof window !== 'undefined') {
            if (typeof window.Onfido === 'undefined') {
                addOnfido(() => {
                    initOnfido();
                });
            } else {
                initOnfido();
            }
        }
        return () => {
            onfidoOutRef.current?.tearDown();
        };
    }, [showOnfido]);

    const initOnfido = () => {
        let options = OnfidoService.getRecipientFraudOptions(extra.docInfo, extra.docs);

        onfidoOutRef.current = OnfidoService.init(window.Onfido, extra.onfidoAccessToken, options, onComplete, onError, 'light');
    };

    const openOnfido = () => {
        setScreen(ONFIDO_SDK);
        setShowOnfido(true);
    };

    const onComplete = () => {
        apiFetchData(`/antifraud/onfido/recipient/uploaded/document`, getRecipientFieldsForOnfido());

        props.toggleModalWithTimer(true, messages.submittedSpinner, () => {
            setScreen(KYC_VERIFYING_SCREEN);
        });
    };

    const onError = (data) => {
        Sentry.captureMessage('Recipient Onfido Frame error', {
            extra: {
                countryId: extra.paymentInfo?.recipientCountry,
                data: data,
            },
            level: 'error',
        });

        setScreen(KYC_FAILED_SCREEN);
    };

    const onBackIconClick = (e) => {
        e.preventDefault();
        setScreen(INTRO_SCREEN);
    };

    const agreementButtonClick = (e) => {
        e.preventDefault();
        amplitude.getInstance().logEvent(AF_RECIPIENT_PERSONAL_DATA_PROCESSING_AGREE_CLICKED, { [SCENARIO_ID]: ANTIFRAUD_SCENARIO_ID });
        setScreen(EMAIL_SCREEN);
    };

    const onClickContinueOnIntro = (e) => {
        e.preventDefault();
        amplitude.getInstance().logEvent(AF_RECIPIENT_IDENTITY_REQUEST_PROMO_CONTINUE_CLICKED, { [SCENARIO_ID]: ANTIFRAUD_SCENARIO_ID });

        if (extra.docs) {
            setScreen(ONFIDO_DOCUMENTS_SCREEN);
        } else if (extra.recipientSumsubEnabled && extra?.recipientAntiFraudStatus?.url != null) {
            props.toggleSpinner(true);
            window.location.href = extra.recipientAntiFraudStatus?.url;
        } else {
            setScreen(AGREEMENT_SCREEN);
        }
    };

    const onClickBackIcon = (e) => {
        e.preventDefault();

        switch (screen) {
            case AGREEMENT_SCREEN:
                setScreen(INTRO_SCREEN);
                break;
            case EMAIL_SCREEN:
                setScreen(AGREEMENT_SCREEN);
                break;
            case ONFIDO_SDK:
                setShowOnfido(false);
                setScreen(ONFIDO_DOCUMENTS_SCREEN);
                break;
            case ONFIDO_DOCUMENT_EXPLANATION_SCREEN:
                setScreen(ONFIDO_DOCUMENTS_SCREEN);
                break;
            case ONFIDO_DOCUMENTS_SCREEN:
                setScreen(extra.fraudOnfidoNeedToRetry ? KYC_FAILED_SCREEN : INTRO_SCREEN);
                break;
            default:
                return null;
        }
    };

    const submitEmailHandler = (fields, json) => {
        amplitude.getInstance().logEvent(AF_RECIPIENT_EMAIL_CONTINUE_CLICKED, { [SCENARIO_ID]: ANTIFRAUD_SCENARIO_ID });

        if (json.extra.docs) {
            new Promise((resolve) => resolve(props.addExtras(json.extra))).then(() => {
                setScreen(ONFIDO_DOCUMENTS_SCREEN);
            });
        } else if (json.extra?.recipientSumsubEnabled && json.extra?.recipientAntiFraudStatus?.url != null) {
            props.toggleSpinner(true);
            window.location.href = json.extra.recipientAntiFraudStatus?.url;
        }
    };

    const getRecipientFieldsForOnfido = () => {
        return [
            {
                name: 'uuid',
                value: paymentInfo.uuid,
            },
            {
                name: 'ticketId',
                value: paymentInfo.ticketId,
            },
            {
                name: 'senderBalId',
                value: paymentInfo.senderBalId,
            },
            {
                name: 'toName',
                value: paymentInfo.toName,
            },
            {
                name: 'toNameF',
                value: paymentInfo.toNameF,
            },
            {
                name: 'recipientCountry',
                value: paymentInfo.recipientCountry,
            },
            {
                name: 'scenarioId',
                value: paymentInfo.scenarioId,
            },
        ];
    };

    const onClickDocument = (docType) => {
        props.toggleSpinner(true);

        apiFetchData(`/antifraud/recipient/onfido/get/document/${docType}`, getRecipientFieldsForOnfido())
            .then((json) => {
                if (json) {
                    if (json.messages) {
                        props.updatePageMessages(json.messages);
                    }
                    if (json.extra) {
                        props.addExtras(json.extra);
                    }
                }
                props.toggleSpinner(false);
                setScreen(ONFIDO_DOCUMENT_EXPLANATION_SCREEN);
            })
            .catch((e) => {
                logInfo(e);
                props.toggleSpinner(false);
                setScreen(ERROR);
            });
    };

    const openHelpCenter = (e) => {
        e.preventDefault();
        const link = getLangCountryPrefix(lang, country) + HELP_MOBILE + extra.helpCenterLink || '';
        amplitude.getInstance().logEvent(GET_HELP_CLICKED, getHelpMobileIdParams(link));
        props.toggleSpinner(true);
        window.location.href = link;
    };

    const openChat = (e) => {
        e.preventDefault();
        props.toggleChat(true);
    };

    const clickOnFailedOnfidoScreen = (e) => {
        if (extra.fraudOnfidoTooManyAttempts) {
            return;
        }
        e.preventDefault();
        if (extra.recipientSumsubEnabled && extra?.recipientAntiFraudStatus?.url != null) {
            props.toggleSpinner(true);
            window.location.href = extra.recipientAntiFraudStatus?.url;
            return;
        }

        if (extra.docs) {
            setScreen(ONFIDO_DOCUMENTS_SCREEN);
        }
    };

    const componentMap = {
        [INTRO_SCREEN]: (
            <CommonPage>
                <InfoPage
                    insideModalComponent={true}
                    iconName="id-card-80"
                    iconColor="primary"
                    headerText={<DangerouslyHtml dangerouslyHtml={messages.introHeaderTitle} />}
                    secondaryText={<DangerouslyHtml dangerouslyHtml={messages.introText} />}
                    primaryButtonCaption={messages.continue}
                    primaryButtonType="link"
                    primaryButtonLink={'#'}
                    primaryButtonOnClick={onClickContinueOnIntro}
                />
            </CommonPage>
        ),
        [AGREEMENT_SCREEN]: (
            <CommonPage backUrl={'/'} onClickIcon={onClickBackIcon}>
                <InfoPage
                    insideModalComponent={true}
                    diskButtonOnClick={onBackIconClick}
                    headerText={messages.disclaimerTitle}
                    secondaryText={<DangerouslyHtml dangerouslyHtml={messages.disclaimerText} />}
                    buttonRowCaption={messages.hrefLabel}
                    buttonRowType="link"
                    buttonRowHref={extra.agreementHref}
                    primaryButtonType="link"
                    primaryButtonCaption={messages.acceptButton}
                    primaryButtonOnClick={agreementButtonClick}
                />
            </CommonPage>
        ),
        [EMAIL_SCREEN]: (
            <CommonPage onClickIcon={onClickBackIcon} backUrl={'/'}>
                <HeaderPanel title={messages.headerEmailScreenTitle} subtitle={messages.headerEmailScreenSubTitle} />
                <Form formName={'recipientEmail'} field={form?.fields} action={'/antifraud/recipient/check.action'} onSubmitHandler={submitEmailHandler}>
                    <InputText field={form?.fields?.email} formName={'recipientEmail'} isNewInput />
                    <FixedBottomPanel>
                        <Button type="submit" raised={true} caption={messages.continue} />
                    </FixedBottomPanel>
                </Form>
            </CommonPage>
        ),
        [ONFIDO_DOCUMENTS_SCREEN]: (
            <CommonPage backUrl={LANDING_LINK} leftCloseIcon onClickIcon={onClickBackIcon}>
                <HeaderPanel title={messages.onfidoTitleWithParams} />
                <OnfidoDocList
                    acceptableDocuments={extra.docs?.acceptableDocuments}
                    onClick={onClickDocument}
                    userCountryId={extra.paymentInfo?.recipientCountry}
                    userCountryInEEA={extra.countryInEEA}
                />
                <Button
                    variant="row"
                    onClick={(e) => {
                        e.preventDefault();
                        props.toggleChat(true);
                    }}
                    caption={messages.needHelpWithDocument}
                    negativeMargin
                />
            </CommonPage>
        ),
        [ONFIDO_DOCUMENT_EXPLANATION_SCREEN]: (
            <CommonPage backUrl={LANDING_LINK} onClickIcon={onClickBackIcon}>
                <HeaderPanel title={messages.suggestionTitle} subtitle={messages.suggestionSubtitle} />
                <MarkedList>
                    {extra.suggestionItems?.map((item, index) => (
                        <MarkedListItem text={item} key={index} />
                    ))}
                </MarkedList>
                <FixedBottomPanel>
                    <Button
                        type="submit"
                        raised={true}
                        caption={messages.suggestionReady}
                        onClick={() => {
                            openOnfido();
                        }}
                    />
                </FixedBottomPanel>
            </CommonPage>
        ),
        [ONFIDO_SDK]: (
            <CommonPage backUrl={LANDING_LINK} onClickIcon={onClickBackIcon}>
                {' '}
                <OnfidoContainer id="onfido-mount" />{' '}
            </CommonPage>
        ),
        [KYC_VERIFYING_SCREEN]: (
            <InfoPage
                iconName="clock-80"
                iconColor="primary"
                headerText={messages.verifyScreenTitle}
                secondaryText={<DangerouslyHtml dangerouslyHtml={messages.verifyScreenText} />}
                primaryButtonCaption={messages.gotItButton}
                primaryButtonType="link"
                primaryButtonLink={LANDING_LINK}
                primaryButtonOnClick={() => {
                    amplitude.getInstance().logEvent(AF_RECIPIENT_ONFIDO_IN_REVIEW_PROMO_DONE_CLICKED, { [SCENARIO_ID]: ANTIFRAUD_SCENARIO_ID });
                }}
            />
        ),
        [KYC_FAILED_SCREEN]: (
            <CommonPage>
                <InfoPage
                    iconName={extra.fraudOnfidoTooManyAttempts ? 'id-card-search-80' : 'id-card-cross-80'}
                    iconColor="primary"
                    headerText={<DangerouslyHtml dangerouslyHtml={messages.onfidoErrorTitle} />}
                    secondaryText={<DangerouslyHtml dangerouslyHtml={messages.onfidoErrorText} />}
                    buttonRowCaption={messages.needHelpWithDocument}
                    buttonRowOnClick={openHelpCenter}
                    buttonRowType="link"
                    primaryButtonCaption={messages.agreeButton}
                    primaryButtonType="link"
                    primaryButtonOnClick={clickOnFailedOnfidoScreen}
                    primaryButtonLink={LANDING_LINK}
                />
            </CommonPage>
        ),
        [EXPIRED_SCREEN]: (
            <CommonPage>
                <InfoPage
                    iconName="alert-circle-80"
                    iconColor="primary"
                    headerText={<DangerouslyHtml dangerouslyHtml={messages.expiredTitle} />}
                    secondaryText={messages.expiredText}
                    primaryButtonCaption={messages.chatButton}
                    primaryButtonType="link"
                    primaryButtonLink={LANDING_LINK}
                    primaryButtonOnClick={openChat}
                />
            </CommonPage>
        ),
    };

    return <>{screen ? componentMap[screen] : null}</>;
};
export default connect(null, {
    updatePageMessages,
    setExtra,
    addExtras,
    toggleSpinner,
    toggleChat,
    toggleModalWithTimer,
})(AntiFraudRecipientFlowPage);
